/**
 * Authentication context.
 *
 * Two providers behind one contract, chosen at build time by whether a Clerk
 * publishable key is present:
 *
 *   - `ClerkAuthProvider` — real sign-in. The identity, roles and permissions all
 *     come from the SERVER (`GET /api/me`), which verified the session token. The
 *     client never decides what it is allowed to do; it asks.
 *   - `DemoAuthProvider`  — the zero-config path, unchanged, for running the app
 *     with no backend at all.
 *
 * The audit's B2 was a *race*: the guard rendered while `roles` was still empty,
 * so a legitimate admin was redirected to the login screen. The fix is structural
 * and is preserved in both providers: `status` only becomes 'ready' AFTER the
 * session AND the roles have both resolved. There is no state in which a
 * signed-in user is visible with unknown roles, so the guard can never make a
 * decision on partial data.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/react';
import { setApiTokenProvider } from './apiToken';
import { getRepo, supabase, isDemoMode, type Me, type Role } from './repo';

export interface AuthUser { id: string; email: string; name: string }

type Status = 'initialising' | 'ready';

interface AuthState {
  status: Status;
  user: AuthUser | null;
  roles: Role[];
  /** Flattened `role_permissions` for this user, as the server reported them. */
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

const SESSION_KEY = 'tsl.demo.session';

/** True when a Clerk publishable key is configured, i.e. real sign-in is on. */
export const clerkEnabled = Boolean(import.meta.env?.VITE_CLERK_PUBLISHABLE_KEY as string | undefined);

/** Demo credentials (only used when Clerk and Supabase are both absent). */
export const DEMO_ACCOUNTS = [
  { email: 'kmec1983@gmail.com', password: 'demo1234', name: 'Iqbal Chand', role: 'admin' as Role },
  { email: 'lakshmi@thestoryline.xyz', password: 'demo1234', name: 'Lakshmi Rao', role: 'editor' as Role },
  { email: 'vikram@thestoryline.xyz', password: 'demo1234', name: 'Vikram Menon', role: 'writer' as Role },
];

/**
 * Local fixture mirroring the `role_permissions` seed in 0001_init.sql, so the
 * demo path behaves like the real one. It is a copy of data the database owns —
 * in Clerk mode the server is the only source of truth.
 */
const DEMO_PERMISSIONS: Record<Role, string[]> = {
  admin: ['articles.read', 'articles.create', 'articles.edit', 'articles.review', 'articles.publish', 'articles.delete', 'media.upload', 'analytics.read', 'users.manage', 'social.manage', 'settings.manage', 'audit_logs.read'],
  editor: ['articles.read', 'articles.create', 'articles.edit', 'articles.review', 'articles.publish', 'media.upload', 'analytics.read', 'seo.manage'],
  writer: ['articles.read', 'articles.create', 'articles.edit', 'media.upload'],
  analyst: ['articles.read', 'analytics.read'],
  social_manager: ['social.manage'],
  seo_manager: ['seo.manage', 'articles.read', 'articles.edit'],
};

function permissionsFor(roles: Role[]): string[] {
  const out = new Set<string>();
  for (const role of roles) for (const p of DEMO_PERMISSIONS[role] ?? []) out.add(p);
  return [...out];
}

// ---------------------------------------------------------------- Clerk ------

function ClerkAuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken, signOut: clerkSignOut } = useClerkAuth();

  const [status, setStatus] = useState<Status>('initialising');
  const [me, setMe] = useState<Me | null>(null);

  // Register the token seam. `getToken` is stable, but re-registering on change
  // is harmless and keeps the provider pointed at the live session.
  useEffect(() => {
    setApiTokenProvider(() => getToken());
    return () => setApiTokenProvider(null);
  }, [getToken]);

  // Resolve session AND server identity together, then flip to ready.
  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      // Wait for Clerk before deciding anything — deciding on `isSignedIn ===
      // false` while the SDK is still loading is exactly the old B2 race.
      if (!isLoaded) {
        if (!cancelled) setStatus('initialising');
        return;
      }
      if (!isSignedIn) {
        if (!cancelled) {
          setMe(null);
          setStatus('ready');
        }
        return;
      }
      try {
        const who = await getRepo().getMe();
        if (!cancelled) {
          setMe(who);
          setStatus('ready');
        }
      } catch {
        // A 401 here means the token did not satisfy the server, so treat it as
        // signed out rather than as a signed-in user with unknown roles.
        if (!cancelled) {
          setMe(null);
          setStatus('ready');
        }
      }
    }

    void resolve();
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, getToken]);

  const signOut = useCallback(async () => {
    await clerkSignOut();
    setMe(null);
    setStatus('ready');
  }, [clerkSignOut]);

  // Sign-in happens through Clerk's own <SignIn/> component, so this exists only
  // to satisfy the shared contract that consumers already rely on.
  const signIn = useCallback(async () => ({
    error: 'Use the sign-in form on this page.',
  }), []);

  const user: AuthUser | null = me ? { id: me.id, email: me.email, name: me.name } : null;
  const roles = (me?.roles ?? []) as Role[];
  const permissions = me?.permissions ?? [];
  const hasPermission = useCallback((p: string) => permissions.includes(p), [permissions]);

  const value = useMemo<AuthState>(
    () => ({ status, user, roles, permissions, hasPermission, signIn, signOut }),
    [status, user, roles, permissions, hasPermission, signIn, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ----------------------------------------------------------------- Demo ------

async function fetchDemoRoles(userId: string, email: string): Promise<Role[]> {
  if (supabase) {
    const { data } = await supabase.from('user_roles').select('role').eq('user_id', userId);
    return ((data ?? []) as { role: Role }[]).map((r) => r.role);
  }
  const acct = DEMO_ACCOUNTS.find((a) => a.email.toLowerCase() === email.toLowerCase());
  return acct ? [acct.role] : [];
}

function DemoAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('initialising');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);

  // Resolve session + roles TOGETHER, then flip to ready.
  const bootstrap = useCallback(async () => {
    setStatus('initialising');
    try {
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        const s = data.session;
        if (s?.user) {
          const r = await fetchDemoRoles(s.user.id, s.user.email ?? '');
          setUser({ id: s.user.id, email: s.user.email ?? '', name: (s.user.user_metadata?.display_name as string) ?? s.user.email ?? '' });
          setRoles(r);
        } else {
          setUser(null);
          setRoles([]);
        }
      } else {
        const raw = localStorage.getItem(SESSION_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as AuthUser;
          const r = await fetchDemoRoles(parsed.id, parsed.email);
          setUser(parsed);
          setRoles(r);
        } else {
          setUser(null);
          setRoles([]);
        }
      }
    } catch {
      setUser(null);
      setRoles([]);
    } finally {
      setStatus('ready');
    }
  }, []);

  useEffect(() => {
    void bootstrap();
    if (!supabase) return;
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      void bootstrap();
    });
    return () => sub.subscription.unsubscribe();
  }, [bootstrap]);

  const signIn = useCallback(async (email: string, password: string) => {
    if (supabase) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };
      await bootstrap();
      return { error: null };
    }
    const acct = DEMO_ACCOUNTS.find((a) => a.email.toLowerCase() === email.trim().toLowerCase());
    if (!acct || acct.password !== password) return { error: 'Invalid email or password.' };
    const u: AuthUser = { id: `demo-${acct.role}`, email: acct.email, name: acct.name };
    localStorage.setItem(SESSION_KEY, JSON.stringify(u));
    await bootstrap();
    return { error: null };
  }, [bootstrap]);

  const signOut = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
    setRoles([]);
    setStatus('ready');
  }, []);

  const permissions = useMemo(() => permissionsFor(roles), [roles]);
  const hasPermission = useCallback((p: string) => permissions.includes(p), [permissions]);

  const value = useMemo<AuthState>(
    () => ({ status, user, roles, permissions, hasPermission, signIn, signOut }),
    [status, user, roles, permissions, hasPermission, signIn, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ------------------------------------------------------------ entry point ----

/**
 * Chooses the provider by configuration rather than at runtime, so neither
 * provider ever calls a hook conditionally.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  return clerkEnabled
    ? <ClerkAuthProvider>{children}</ClerkAuthProvider>
    : <DemoAuthProvider>{children}</DemoAuthProvider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function hasAnyRole(roles: Role[], allowed: Role[]): boolean {
  return roles.some((r) => allowed.includes(r));
}

/**
 * Guard for /admin. Decision order is what matters:
 *   1. still bootstrapping  -> render a loader, never redirect
 *   2. no user              -> go to login
 *   3. user but no role     -> explicit 403 (not a redirect loop)
 *   4. otherwise            -> render
 */
export function RequireRole({ allow, children }: { allow: Role[]; children: ReactNode }) {
  const { status, user, roles } = useAuth();
  const location = useLocation();

  if (status === 'initialising') {
    return (
      <div className="min-h-screen grid place-items-center bg-background" role="status" aria-live="polite">
        <div className="flex flex-col items-center gap-3 text-muted">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" />
          <span className="text-sm">Checking access…</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  if (!hasAnyRole(roles, allow)) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-display font-semibold">403 — Not authorised</h1>
          <p className="text-muted text-sm">
            You are signed in as <strong>{user.email}</strong>, but your account does not have access to this area.
            Ask an administrator to grant a role.
          </p>
          <p className="text-xs text-muted">Signed-in roles: {roles.length ? roles.join(', ') : 'none'}</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

export function useRepoKind() {
  return getRepo().kind;
}

export { isDemoMode };
